import React, { useState, useEffect, useContext } from "react";
import * as S from "./NewContractStyle";
import { AuthContext } from "../../../Context/AuthContext";
import { formatCPF, formatNumber } from "../../ASSETS/assets";
import axios from "axios";
import { FaSearch, FaTimes } from "react-icons/fa";
import Loading from "../../Loader";
import { useMessage } from "../../../Context/MessageContext";

const base_url = process.env.REACT_APP_API_BASE_URL;

function ClientModal({ onSelectClient, onClose }) {
    const { getClients, clientsDataBase } = useContext(AuthContext);
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingSearch, setLoadingSearch] = useState(false);

    const handleSearch = async () => {
        setLoadingSearch(true);
        try {
            await getClients(50, 0, searchTerm, "asc", "");
        } catch (error) {
            console.error("Erro ao buscar clientes:", error);
        } finally {
            setLoadingSearch(false);
        }
    };

    useEffect(() => {
        handleSearch();
    }, []);

    const handleKeyPress = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    return (
        <S.ModalOverlay onClick={onClose}>
            <S.ModalContent onClick={(e) => e.stopPropagation()}>
                <S.ModalHeader>
                    <h3>Selecionar Cliente</h3>
                    <S.CloseButton onClick={onClose}><FaTimes /></S.CloseButton>
                </S.ModalHeader>
                <S.SearchWrapper>
                    <S.SearchInput
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Pesquisar por nome ou CPF..."
                    />
                    <S.SearchButton onClick={handleSearch}><FaSearch /></S.SearchButton>
                </S.SearchWrapper>
                <S.ClientList>
                    {loadingSearch && <p style={{ textAlign: "center", color: "#888", padding: "20px" }}>Buscando...</p>}
                    {!loadingSearch && clientsDataBase.map((client) => (
                        <S.ClientItem key={client.id} onClick={() => { onSelectClient(client); onClose(); }}>
                            <p>{client.name}</p>
                            <span>{formatCPF(client.cpfCnpj)}</span>
                        </S.ClientItem>
                    ))}
                    {!loadingSearch && clientsDataBase.length === 0 && (
                        <p style={{ textAlign: "center", color: "#888", padding: "20px" }}>Nenhum cliente encontrado.</p>
                    )}
                </S.ClientList>
            </S.ModalContent>
        </S.ModalOverlay>
    );
}

export default function NewContract({ handleClose }) {
    const { authState } = useContext(AuthContext);
    const { showMessage } = useMessage();
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);

    const [selectedClient, setSelectedClient] = useState(null);
    const [contractQtt, setContractQtt] = useState(1);
    const [contractUniValue, setContractUniValue] = useState(425.00);
    const [maxValorization, setMaxValorization] = useState(147);
    const [duration, setDuration] = useState(36);
    const [payMethod, setPayMethod] = useState("");
    const [status, setStatus] = useState("");
    const [isRepurchase, setIsRepurchase] = useState(false);
    const [notes, setNotes] = useState("");

    const handleCreateContract = async () => {
        if (!selectedClient) return showMessage("Selecione um cliente.", "warning");
        if (!payMethod) return showMessage("Selecione um método de pagamento.", "warning");
        if (!status) return showMessage("Selecione um status inicial.", "warning");

        const payload = {
            clientId: selectedClient.id,
            contractQtt: parseInt(contractQtt),
            contractUniValue: parseFloat(contractUniValue),
            maxValorization: parseInt(maxValorization),
            duration: parseInt(duration),
            payMethod,
            status: parseInt(status),
            notes: notes || null,
        };

        setIsLoading(true);
        try {
            await axios.post(`${base_url}contract/admin?isRepurchase=${isRepurchase}`, payload, {
                headers: { Authorization: `Bearer ${authState.accessToken}` },
            });
            showMessage("Contrato criado com sucesso!", "success");
            handleClose();
        } catch (error) {
            console.error("Erro ao criar contrato:", error);
            const errorMsg = error.response?.data?.message || "Não foi possível criar o contrato.";
            showMessage(errorMsg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <S.PageContainer>
                <Loading load={isLoading} />
                <S.ContentBox>
                    <S.Header>
                        <h2>Criar Novo <span>Contrato</span></h2>
                        <S.CloseButton onClick={handleClose}><FaTimes /></S.CloseButton>
                    </S.Header>

                    <S.FormBody>
                        <S.InputGroup style={{ gridColumn: "1 / -1" }}>
                            <label>Cliente</label>
                            <S.SelectClientBox onClick={() => setModalOpen(true)}>
                                <span>{selectedClient ? `${selectedClient.name} — ${formatCPF(selectedClient.cpfCnpj)}` : "Clique para selecionar um cliente"}</span>
                                <FaSearch />
                            </S.SelectClientBox>
                        </S.InputGroup>

                        <S.FormGrid>
                            <S.InputGroup>
                                <label>Qtd. Cotas</label>
                                <input type="number" value={contractQtt} onChange={(e) => setContractQtt(e.target.value)} />
                            </S.InputGroup>
                            <S.InputGroup>
                                <label>Valor Unitário (R$)</label>
                                <input type="number" value={contractUniValue} onChange={(e) => setContractUniValue(e.target.value)} />
                            </S.InputGroup>
                            <S.InputGroup>
                                <label>Valor Total</label>
                                <input value={`R$ ${formatNumber(contractQtt * contractUniValue)}`} readOnly />
                            </S.InputGroup>
                            <S.InputGroup>
                                <label>Valorização Final (%)</label>
                                <input type="number" value={maxValorization} disabled={true} />
                            </S.InputGroup>
                            <S.InputGroup>
                                <label>Lucro Estimado</label>
                                <input value={`R$ ${formatNumber((contractQtt * contractUniValue) * (maxValorization / 100))}`} readOnly />
                            </S.InputGroup>
                            <S.InputGroup>
                                <label>Duração (Meses)</label>
                                <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} />
                            </S.InputGroup>
                        </S.FormGrid>

                        <div>
                            <S.SectionLabel>Método de Pagamento</S.SectionLabel>
                            <S.RadioGroup>
                                {["Pix", "Boleto", "Depósito"].map((method) => (
                                    <S.RadioCard key={method} checked={payMethod === method}>
                                        <input type="radio" value={method} name="payMethod" onChange={(e) => setPayMethod(e.target.value)} />
                                        {method}
                                    </S.RadioCard>
                                ))}
                            </S.RadioGroup>
                        </div>

                        <div>
                            <S.SectionLabel>Status Inicial</S.SectionLabel>
                            <S.RadioGroup>
                                <S.RadioCard checked={status === "4"}>
                                    <input type="radio" value="4" name="status" onChange={(e) => setStatus(e.target.value)} />
                                    Pendente
                                </S.RadioCard>
                                <S.RadioCard checked={status === "1"}>
                                    <input type="radio" value="1" name="status" onChange={(e) => setStatus(e.target.value)} />
                                    Pago (Inicia Valorização)
                                </S.RadioCard>
                            </S.RadioGroup>
                        </div>
                        <div>
                            <S.SectionLabel>Tipo de Contrato</S.SectionLabel>
                            <S.RadioGroup>
                                <S.RadioCard checked={!isRepurchase} onClick={() => setIsRepurchase(false)}>
                                    <input type="radio" name="isRepurchase" checked={!isRepurchase} onChange={() => setIsRepurchase(false)} />
                                    Novo Contrato
                                </S.RadioCard>
                                <S.RadioCard checked={isRepurchase} onClick={() => setIsRepurchase(true)}>
                                    <input type="radio" name="isRepurchase" checked={isRepurchase} onChange={() => setIsRepurchase(true)} />
                                    Recompra
                                </S.RadioCard>
                            </S.RadioGroup>
                        </div>

                        <S.InputGroup>
                            <label>Anotações (opcional)</label>
                            <S.TextArea
                                placeholder="Adicione observações sobre este contrato..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </S.InputGroup>
                    </S.FormBody>

                    <S.Footer>
                        <S.ConfirmButton onClick={handleCreateContract} disabled={isLoading}>
                            {isLoading ? "Criando..." : "Confirmar e Criar Contrato"}
                        </S.ConfirmButton>
                    </S.Footer>
                </S.ContentBox>
            </S.PageContainer>

            {isModalOpen && <ClientModal onSelectClient={setSelectedClient} onClose={() => setModalOpen(false)} />}
        </>
    );
}
